import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Observable } from 'rxjs';

import { serviceurl } from 'src/app/constant/constant';
import { SKIP_ERROR_TOAST } from 'src/app/interceptors/error.interceptor';
import { ConfirmModalComponent } from 'src/app/modal/confirm-modal/confirm-modal.component';
import { AuthServices } from 'src/app/services/auth.service';
import { AuthTokenService } from 'src/app/services/auth-token.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { DataService } from 'src/app/services/data.service';
import { IntroService } from 'src/app/services/intro.service';
import { SubscriptionService } from '../subscription/services/subscription.service';
import { isAuthPage } from './auth-pages';

/** What the backend answers when it signs someone in (api/auth/*). */
export interface SessionPayload {
  id: number;
  first_name: string;
  email: string;
  accesstoken: string;
  refreshtoken: string;
  [key: string]: any;
}

/** Sign-in answer for an account from Auth0 that must choose a new password first. */
export interface PasswordChangeRequired {
  password_change_required: true;
  ticket: string;
  first_name: string;
  email: string;
}

export type LoginResult = SessionPayload | PasswordChangeRequired;

/** A failed call, reduced to what the pages show. */
export interface AuthError {
  code: string;
  message: string;
  problems?: string[];
  email?: string;
  retryAfter?: number;
}

const RETURN_KEY = 'wt_return_to';
const OFFLINE = "We can't reach WorldTumbler right now. Check your connection and try again.";
const SERVER = 'Something went wrong on our side. Please try again in a moment.';

/**
 * WorldTumbler's own sign-in (backend: fantasyapp/views/auth.py).
 *
 * The pages show their own errors next to the form, so these calls skip the
 * global error toast. A successful sign-in goes through `startSession`, which
 * saves the login tokens, loads the profile and plan, and sends the user on
 * to where they were going.
 */
@Injectable({ providedIn: 'root' })
export class AccountAuthService {
  private readonly api = `${serviceurl}api/auth/`;
  private readonly quiet = () => ({ context: new HttpContext().set(SKIP_ERROR_TOAST, true) });

  /**
   * Set by the sign-in page when an account from Auth0 must choose a new
   * password; read by the set-new-password page. Memory only (never stored):
   * a reload means signing in again.
   */
  pendingChange: (PasswordChangeRequired & { currentPassword: string }) | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokens: AuthTokenService,
    private profile: AuthServices,
    private currentUser: CurrentUserService,
    private subscriptions: SubscriptionService,
    private data: DataService,
    private intro: IntroService,
    private modals: NgbModal,
  ) {}

  // ---- calls -------------------------------------------------------------------

  login(email: string, password: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(`${this.api}login/`, { email, password }, this.quiet());
  }

  signup(body: { first_name: string; last_name: string; email: string; password: string; accept_terms: boolean }) {
    return this.http.post<{ message: string; email: string }>(`${this.api}signup/`, body, this.quiet());
  }

  verifyEmail(token: string) {
    return this.http.post<SessionPayload | { already_verified: true; message: string }>(
      `${this.api}verify-email/`, { token }, this.quiet());
  }

  resendVerification(email: string) {
    return this.http.post<{ message: string }>(`${this.api}resend-verification/`, { email }, this.quiet());
  }

  firstPasswordChange(ticket: string, newPassword: string) {
    return this.http.post<SessionPayload>(
      `${this.api}password/first-change/`, { ticket, new_password: newPassword }, this.quiet());
  }

  forgotPassword(email: string) {
    return this.http.post<{ message: string }>(`${this.api}password/forgot/`, { email }, this.quiet());
  }

  checkResetLink(uid: string, token: string) {
    return this.http.post<{ valid: true; email: string }>(
      `${this.api}password/reset/`, { uid, token, check_only: true }, this.quiet());
  }

  resetPassword(uid: string, token: string, password: string) {
    return this.http.post<SessionPayload>(`${this.api}password/reset/`, { uid, token, password }, this.quiet());
  }

  /** Signed in: change password. This device stays signed in (new tokens). */
  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post<SessionPayload>(
      `${this.api}password/change/`,
      { current_password: currentPassword, new_password: newPassword },
      this.quiet(),
    );
  }

  // ---- errors ------------------------------------------------------------------

  toError(err: unknown): AuthError {
    if (!(err instanceof HttpErrorResponse)) return { code: 'unknown', message: SERVER };
    if (err.status === 0) return { code: 'offline', message: OFFLINE };
    const body = err.error && typeof err.error === 'object' ? err.error : {};
    return {
      code: body.code || (err.status >= 500 ? 'server_error' : 'error'),
      message: (typeof body.error === 'string' && body.error) || SERVER,
      problems: Array.isArray(body.problems) ? body.problems : undefined,
      email: body.email,
      retryAfter: body.retry_after,
    };
  }

  // ---- where to go after signing in -------------------------------------------------

  /** Remember a page to come back to after signing in (e.g. from a guard). */
  rememberReturnTo(url: string | null | undefined): void {
    if (!url || !url.startsWith('/') || url.startsWith('//') || isAuthPage(url)) return;
    try {
      sessionStorage.setItem(RETURN_KEY, url);
    } catch {
      /* storage unavailable */
    }
  }

  private takeReturnTo(): string {
    try {
      const url = sessionStorage.getItem(RETURN_KEY);
      sessionStorage.removeItem(RETURN_KEY);
      if (url && url.startsWith('/') && !url.startsWith('//') && !isAuthPage(url)) return url;
    } catch {
      /* storage unavailable */
    }
    return '/home';
  }

  // ---- session -----------------------------------------------------------------

  /**
   * Signs the user in with what the backend returned: saves the login tokens
   * (before anything else is requested, or those calls go out unsigned),
   * loads the profile, plan and notification count, then opens the page they
   * were heading to.
   */
  async startSession(session: SessionPayload, options: { welcome?: boolean } = {}): Promise<void> {
    this.pendingChange = null;
    this.tokens.setTokens(session.accesstoken, session.refreshtoken);

    const profile: any = await firstValueFrom(this.profile.currentUserData(session.id));
    // Keep personal and billing details out of localStorage.
    const { stripe_customer_id, email, ...safeProfile } = profile || {};
    localStorage.setItem('currentUser', JSON.stringify(safeProfile));
    this.currentUser.setUserData(safeProfile);

    this.data.getNotificationCount(session.id).subscribe({
      next: (res: { count: number }) => this.currentUser.setCount(res.count),
      error: () => {},
    });
    this.subscriptions.getSubscriptionByIdForUsage(session.id).subscribe({
      next: (res) => {
        if (res) {
          sessionStorage.setItem('planUsage', JSON.stringify(res));
          this.currentUser.setPlanUsageData(res);
        }
      },
      error: () => {},
    });

    const target = this.takeReturnTo();
    await this.router.navigateByUrl(target);
    if (target === '/home') {
      // The logo intro over /home, then the beta notice (as before).
      this.intro.play().then(() => this.betaNotice(options.welcome));
    }
  }

  /**
   * Signs out on this device: cancels the refresh token on the server and
   * forgets everything kept for the session, then opens `goTo` (with an
   * optional page to come back to after the next sign-in).
   */
  signOut(goTo: string | null = '/home', options: { returnTo?: string; queryParams?: Record<string, string> } = {}): void {
    this.tokens.revoke();
    this.pendingChange = null;
    try {
      sessionStorage.clear();
      localStorage.clear();
    } catch {
      /* storage unavailable */
    }
    this.currentUser.setCount(0);
    this.currentUser.setUserData(null);
    this.currentUser.setPlanUsageData(null);
    if (options.returnTo) this.rememberReturnTo(options.returnTo);
    if (goTo) this.router.navigate([goTo], { queryParams: options.queryParams });
  }

  private betaNotice(welcome = false): void {
    const ref = this.modals.open(ConfirmModalComponent, { size: 'md', backdrop: 'static', centered: true });
    ref.componentInstance.title = welcome ? 'Welcome to WorldTumbler' : 'WorldTumbler Beta Is Live';
    ref.componentInstance.message =
      'We appreciate you joining our early access community. See something odd? Report bugs to support@worldtumbler.com and help us improve!';
    ref.componentInstance.confirmLabel = 'Continue';
    ref.componentInstance.confirmVariant = 'theme';
    ref.componentInstance.showCancel = false;
    ref.componentInstance.confirmClick.subscribe(() => ref.close('navigate'));
  }
}
